import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Bot, Save, RotateCcw, Loader2, CheckCircle, AlertTriangle,
  Info, Plus, FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SystemPromptEditor = ({ authToken, userRole, projectId = "hypefactory" }) => {
  const [promptSettings, setPromptSettings] = useState({
    base_prompt: "",
    additional_instructions: "",
    updated_at: null,
    updated_by: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [currentProject, setCurrentProject] = useState(projectId);

  // Sync internal state when parent project changes
  useEffect(() => {
    setCurrentProject(projectId);
  }, [projectId]);

  const projects = [
    { id: "hypefactory", name: "HypeFactory", color: "#3B82F6" },
    { id: "factoryhype", name: "FactoryHype", color: "#10B981" },
    { id: "factoryhype_v2", name: "FactoryHype V2", color: "#EC4899" },
    { id: "hypeconnect", name: "HypeConnect", color: "#6366F1" }
  ];

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchPromptSettings();
  }, [currentProject]);

  const fetchPromptSettings = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/system-prompt?project_id=${currentProject}`, getAuthHeaders());
      setPromptSettings(response.data);
      setOriginalPrompt(response.data.base_prompt);
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching prompt:", error);
      toast.error("Fehler beim Laden des System-Prompts");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/system-prompt?project_id=${currentProject}`, {
        base_prompt: promptSettings.base_prompt,
        additional_instructions: promptSettings.additional_instructions
      }, getAuthHeaders());
      toast.success(`${projects.find(p => p.id === currentProject)?.name || "Projekt"} System-Prompt gespeichert!`);
      setHasChanges(false);
      setOriginalPrompt(promptSettings.base_prompt);
      fetchPromptSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await axios.post(`${API}/admin/system-prompt/reset?project_id=${currentProject}`, null, getAuthHeaders());
      toast.success("Basis-Prompt zurückgesetzt.");
      setResetDialogOpen(false);
      fetchPromptSettings();
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen");
    }
  };

  const handleBasePromptChange = (value) => {
    setPromptSettings(prev => ({ ...prev, base_prompt: value }));
    setHasChanges(value !== originalPrompt || promptSettings.additional_instructions !== "");
  };

  const handleAdditionalChange = (value) => {
    setPromptSettings(prev => ({ ...prev, additional_instructions: value }));
    setHasChanges(true);
  };

  if (userRole !== "admin") {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="py-16 text-center">
          <Bot className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nur Admins können den System-Prompt bearbeiten.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-prompt-editor">
      {/* Project Switcher */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
        {projects.map(project => (
          <button
            key={project.id}
            onClick={() => {
              if (hasChanges) {
                if (window.confirm("Ungespeicherte Änderungen gehen verloren. Projekt wechseln?")) {
                  setCurrentProject(project.id);
                }
              } else {
                setCurrentProject(project.id);
              }
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              currentProject === project.id
                ? 'bg-white shadow-sm text-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            style={currentProject === project.id ? {
              borderBottom: `2px solid ${project.color}`
            } : {}}
          >
            {project.name}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Bot className="w-6 h-6" style={{ color: projects.find(p => p.id === currentProject)?.color }} />
            KI System-Prompt: {projects.find(p => p.id === currentProject)?.name}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Steuere das Verhalten des {projects.find(p => p.id === currentProject)?.name || "Projekt"} Chatbots.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setResetDialogOpen(true)}
            className="rounded-lg"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Zurücksetzen
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="rounded-lg bg-gradient-to-r from-blue-600 to-violet-600"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>

      {/* Last Update Info */}
      {promptSettings.updated_at && (
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500" />
          Zuletzt geändert: {new Date(promptSettings.updated_at).toLocaleString('de-DE')} von {promptSettings.updated_by}
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Wie funktioniert der System-Prompt?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Der <strong>Basis-Prompt</strong> definiert das grundlegende Verhalten des Chatbots</li>
                <li><strong>Zusätzliche Anweisungen</strong> werden am Ende angehängt für schnelle Anpassungen</li>
                <li>Änderungen werden <strong>sofort für neue Chats</strong> übernommen (kein Deploy nötig)</li>
                <li>Laufende Chats behalten ihren ursprünglichen Prompt</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Prompt Editor */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-400" />
            Basis System-Prompt
          </CardTitle>
          <CardDescription>
            Der vollständige Prompt, der dem KI-Modell das Verhalten vorgibt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={promptSettings.base_prompt}
            onChange={(e) => handleBasePromptChange(e.target.value)}
            className="min-h-[400px] font-mono text-sm resize-y"
            placeholder="System-Prompt eingeben..."
            data-testid="base-prompt-textarea"
          />
          <p className="text-xs text-slate-400 mt-2">
            {promptSettings.base_prompt.length} Zeichen
          </p>
        </CardContent>
      </Card>

      {/* Additional Instructions */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="w-5 h-5 text-slate-400" />
            Zusätzliche Anweisungen
          </CardTitle>
          <CardDescription>
            Schnelle Anpassungen ohne den Basis-Prompt zu ändern. Wird am Ende angehängt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={promptSettings.additional_instructions}
            onChange={(e) => handleAdditionalChange(e.target.value)}
            className="min-h-[150px] font-mono text-sm resize-y"
            placeholder="z.B. 'Erwähne immer das aktuelle Jobangebot in Berlin.' oder 'Sei besonders freundlich zu Berufseinsteigern.'"
            data-testid="additional-instructions-textarea"
          />
          <p className="text-xs text-slate-400 mt-2">
            Diese Anweisungen werden automatisch an den Basis-Prompt angehängt.
          </p>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Accordion type="single" collapsible className="bg-white rounded-xl border border-slate-200">
        <AccordionItem value="tips" className="border-0">
          <AccordionTrigger className="px-6 hover:no-underline">
            <span className="flex items-center gap-2 text-slate-700">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Tipps für einen guten Prompt
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-4">
            <ul className="space-y-2 text-sm text-slate-600">
              <li>✅ <strong>Klar und spezifisch:</strong> Definiere genau, was der Bot sagen soll und was nicht</li>
              <li>✅ <strong>Struktur:</strong> Nutze Überschriften und Aufzählungen für Übersichtlichkeit</li>
              <li>✅ <strong>[SHOW_CONTACT_BUTTON]:</strong> Dieser Marker zeigt den Kontaktdaten-Button an</li>
              <li>✅ <strong>Beispiele:</strong> Gib Beispielantworten für häufige Fragen</li>
              <li>❌ <strong>Keine Gehälter:</strong> Vermeide konkrete Zahlen, die sich ändern können</li>
              <li>❌ <strong>Keine Vertragsdetails:</strong> Das gehört ins persönliche Gespräch</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Reset Dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Basis-Prompt zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Basis-Prompt wird auf die Standardversion zurückgesetzt. 
              <strong className="block mt-2 text-emerald-600">✓ Ihre Qualifizierungsregeln bleiben erhalten!</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="bg-amber-600 hover:bg-amber-700 rounded-lg">
              Zurücksetzen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SystemPromptEditor;
